'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import styles from './styles.module.css'
import { getStrapiImageUrl } from '@/lib/strapi'

interface MediaImage {
  url?: string
  alternativeText?: string
  data?: {
    attributes?: {
      url?: string
      alternativeText?: string
    }
  }
}

interface DualImgScreenData {
  left_desk_img?: MediaImage
  right_desk_img?: MediaImage
  left_tablet_img?: MediaImage
  right_tablet_img?: MediaImage
  left_mobile_img?: MediaImage
  right_mobile_img?: MediaImage
}

interface DualImgScreenProps {
  data: DualImgScreenData
}

const extractImageUrl = (img?: MediaImage): { url: string; alt: string } => {
  if (!img) return { url: '', alt: '' }

  if (img.data?.attributes?.url) {
    return {
      url: getStrapiImageUrl(img.data.attributes.url),
      alt: img.data.attributes.alternativeText || ''
    }
  }

  if (img.url) {
    return {
      url: getStrapiImageUrl(img.url),
      alt: img.alternativeText || ''
    }
  }

  return { url: '', alt: '' }
}

const DualImgScreen: React.FC<DualImgScreenProps> = ({ data }) => {
  const [screenSize, setScreenSize] = useState<'desktop' | 'tablet' | 'mobile'>('desktop')

  useEffect(() => {
    const checkSize = () => {
      const width = window.innerWidth
      if (width <= 767) {
        setScreenSize('mobile')
      } else if (width <= 1439) {
        setScreenSize('tablet')
      } else {
        setScreenSize('desktop')
      }
    }
    checkSize()
    window.addEventListener('resize', checkSize)
    return () => window.removeEventListener('resize', checkSize)
  }, [])

  const leftDesk = extractImageUrl(data.left_desk_img)
  const rightDesk = extractImageUrl(data.right_desk_img)
  const leftTablet = extractImageUrl(data.left_tablet_img)
  const rightTablet = extractImageUrl(data.right_tablet_img)
  const leftMobile = extractImageUrl(data.left_mobile_img)
  const rightMobile = extractImageUrl(data.right_mobile_img)

  // Выбор левого изображения с fallback
  let leftImage = leftDesk
  if (screenSize === 'mobile') {
    leftImage = leftMobile.url ? leftMobile : (leftTablet.url ? leftTablet : leftDesk)
  } else if (screenSize === 'tablet') {
    leftImage = leftTablet.url ? leftTablet : leftDesk
  }

  // Выбор правого изображения с fallback
  let rightImage = rightDesk
  if (screenSize === 'mobile') {
    rightImage = rightMobile.url ? rightMobile : (rightTablet.url ? rightTablet : rightDesk)
  } else if (screenSize === 'tablet') {
    rightImage = rightTablet.url ? rightTablet : rightDesk
  }

  if (!leftImage.url && !rightImage.url) return null

  return (
    <section className={styles.dualImgScreen}>
      {leftImage.url && (
        <div className={styles.imageWrapper}>
          <Image
            src={leftImage.url}
            alt={leftImage.alt || ''}
            width={540}
            height={400}
            className={styles.image}
          />
        </div>
      )}
      {rightImage.url && (
        <div className={styles.imageWrapper}>
          <Image
            src={rightImage.url}
            alt={rightImage.alt || ''}
            width={540}
            height={400}
            className={styles.image}
          />
        </div>
      )}
    </section>
  )
}

DualImgScreen.displayName = 'DualImgScreen'

export default DualImgScreen

